import { supabase } from '@/integrations/supabase/client';

/**
 * TIMER V2 — 8G.2C.5: THE CUSTOMER COLLECTED A FINISHED VEHICLE.
 *
 * Both surfaces used to call `move_vehicle_location(..., p_is_pickup: true)`
 * and read the answer as `rawData as unknown as { success: boolean }`. That RPC
 * is SECURITY DEFINER, so it ran as `postgres` and every direct-DML guard added
 * in 8G.2C.3/8G.2C.4 waved it through — they exist to stop a BROWSER writing
 * terminal facts, and this was the browser writing one through a staff door.
 *
 * MEASURED before the fix, as a real authenticated MANAGER: it succeeded on an
 * Active ticket with a RUNNING timer, on a paused episode, and on one still
 * owing an allocation review. A service advisor could hand a car back while a
 * technician was measuring work on it.
 *
 * `mark_completed_ticket_picked_up` requires the visit to be canonically
 * Completed AND Timer V2 quiescent, under the shared ticket transition lock.
 *
 * DISTINCT FROM `markPickedUpIncomplete`. That records a different business
 * fact — the customer took the car BEFORE the work was done — and lands the
 * ticket in 'Blocked'. This one leaves a finished visit finished. They must not
 * be merged.
 */
export type CompletedPickupOutcome =
  /** The vehicle left. Physical departure recorded; no completion fact touched. */
  | 'PICKED_UP'
  /**
   * It had already gone. A retry after a lost response, or a colleague who got
   * there first — a recovery, not an error, and NOT a second handover.
   */
  | 'ALREADY_PICKED_UP'
  /** The visit is not finished. Picked Up Incomplete is the other act. */
  | 'NOT_COMPLETED'
  /** Completed in name, but technician evidence is still open. A person decides. */
  | 'TIMER_EVIDENCE_CONFLICT'
  /**
   * Completed in name, but the visit does not satisfy the requirements for
   * completing a visit — an open job, a diagnostic outcome owed, an unresolved
   * comeback. The same obligations canonical completion checks.
   */
  | 'COMPLETION_OBLIGATION_CONFLICT'
  /** Unknown, another tenant's, or one this caller may not act on — one answer. */
  | 'TICKET_NOT_AVAILABLE'
  /** The physical move itself failed. */
  | 'INTEGRITY_ERROR'
  /** No server answer. Does NOT mean nothing committed. */
  | 'SUBMIT_FAILED';

export interface CompletedPickupResult {
  outcome: CompletedPickupOutcome;
  ticketId: string;
  /** Set on PICKED_UP and on ALREADY_PICKED_UP — the ORIGINAL instant. */
  pickedUpAt: string | null;
  /** Canonical shop, resolved and authorized server-side under the lock. */
  shopId: string | null;
  status: string | null;
  activeSessions: number;
  unresolvedPauses: number;
  awaitingReview: number;
  note: string | null;
}

const KNOWN: CompletedPickupOutcome[] = [
  'PICKED_UP', 'ALREADY_PICKED_UP', 'NOT_COMPLETED', 'TIMER_EVIDENCE_CONFLICT',
  'COMPLETION_OBLIGATION_CONFLICT', 'TICKET_NOT_AVAILABLE', 'INTEGRITY_ERROR',
  'SUBMIT_FAILED',
];

export async function markCompletedTicketPickedUp(
  ticketId: string,
): Promise<CompletedPickupResult> {
  const { data, error } = await (supabase as any).rpc('mark_completed_ticket_picked_up', {
    p_ticket_id: ticketId,
  });

  const empty = {
    ticketId, pickedUpAt: null, shopId: null, status: null,
    activeSessions: 0, unresolvedPauses: 0, awaitingReview: 0,
  };
  if (error) {
    console.warn('[markCompletedTicketPickedUp] failed', error);
    return { ...empty, outcome: 'SUBMIT_FAILED', note: error.message };
  }

  const r = (data ?? {}) as Record<string, unknown>;
  // Unknown outcomes FAIL CLOSED. The vocabulary belongs to the server, and a
  // client too old to understand a new refusal must not report it as a
  // completed handover.
  let outcome = KNOWN.includes(r.outcome as CompletedPickupOutcome)
    ? (r.outcome as CompletedPickupOutcome)
    : 'SUBMIT_FAILED';

  const pickedUpAt = (r.picked_up_at as string) ?? null;
  const shopId = (r.shop_id as string) ?? null;

  // A SETTLED RESULT MUST BE INTERNALLY CONSISTENT — the same rule 8G.2C.4 §16
  // applied to Restore. A settled answer with no instant and no shop cannot be
  // attributed, and the surfaces build their downstream OCIS record from
  // exactly those fields.
  //
  // 8G.2C.6 adds `status === 'Completed'`, and that is the load-bearing part.
  // `picked_up_at` is a PHYSICAL marker shared with Picked Up Incomplete, so
  // instant-plus-shop was satisfied by a response that said:
  //
  //     { outcome: 'ALREADY_PICKED_UP', picked_up_at: …, shop_id: …,
  //       status: 'Blocked' }
  //
  // — a settled COMPLETED pickup for a car that left with work outstanding. The
  // server no longer produces that, but the client must not be the only thing
  // standing between a contradictory response and a durable OCIS record: a
  // settled completed pickup is Completed, by definition, and anything else
  // fails closed to a reload.
  const status = (r.status as string) ?? null;
  if ((outcome === 'PICKED_UP' || outcome === 'ALREADY_PICKED_UP')
      && !(pickedUpAt && shopId && status === 'Completed')) {
    console.warn('[markCompletedTicketPickedUp] settled without canonical attribution', {
      ticketId, pickedUpAt, shopId, status,
    });
    outcome = 'SUBMIT_FAILED';
  }

  return {
    outcome,
    ticketId: (r.ticket_id as string) ?? ticketId,
    pickedUpAt,
    shopId,
    status,
    activeSessions: Number(r.active_sessions ?? 0),
    unresolvedPauses: Number(r.unresolved_pauses ?? 0),
    awaitingReview: Number(r.awaiting_review ?? 0),
    note: (r.note as string) ?? null,
  };
}

/**
 * The vehicle is gone — either now or already. Use this to decide whether to
 * REFRESH.
 */
export const isPickupSettled = (o: CompletedPickupOutcome) =>
  o === 'PICKED_UP' || o === 'ALREADY_PICKED_UP';

/**
 * THIS handover just happened, here, in response to THIS click.
 *
 * The distinction from `isPickupSettled` is the whole point: durable
 * downstream side effects are gated on this, so `ALREADY_PICKED_UP` refreshes
 * the board WITHOUT emitting a second human pickup event for a car that left
 * once.
 */
export const didPickUpNow = (o: CompletedPickupOutcome) => o === 'PICKED_UP';

/**
 * The server may or may not have committed. Reload; do not replay.
 *
 * A handover is a human act with a real-world referent — the customer either
 * drove away or did not. Re-firing it because a response was lost risks
 * recording a second departure for one car. Reload and let the operator see
 * what is true.
 */
export const needsPickupReload = (o: CompletedPickupOutcome) => o === 'SUBMIT_FAILED';

export function describePickupRefusal(r: CompletedPickupResult): string {
  switch (r.outcome) {
    case 'NOT_COMPLETED':
      return r.status === 'Blocked'
        ? 'This visit is not finished. It was already picked up incomplete.'
        : 'This visit is not finished yet, so it cannot be handed back as a completed pickup. '
          + 'Complete it first, or use Mark Picked Up (Incomplete) if the customer is taking '
          + 'the vehicle before the work is done.';
    case 'TIMER_EVIDENCE_CONFLICT': {
      const bits: string[] = [];
      if (r.activeSessions > 0) bits.push(`${r.activeSessions} technician timer(s) still running`);
      if (r.unresolvedPauses > 0) bits.push(`${r.unresolvedPauses} unresolved pause(s)`);
      if (r.awaitingReview > 0) bits.push(`${r.awaitingReview} episode(s) awaiting review`);
      const detail = bits.length ? ` (${bits.join(', ')})` : '';
      return `This visit is marked completed but still has outstanding technician work${detail}. `
        + 'Someone needs to resolve that before the vehicle leaves.';
    }
    case 'COMPLETION_OBLIGATION_CONFLICT':
      return 'This visit is marked completed but does not meet the requirements for '
        + `a completed visit${r.note ? '' : ''}. Someone needs to reconcile it before `
        + 'the vehicle leaves.';
    case 'TICKET_NOT_AVAILABLE':
      return 'This ticket is not available.';
    case 'INTEGRITY_ERROR':
      return r.note ?? 'The vehicle could not be released from its location.';
    case 'SUBMIT_FAILED':
      return 'The result could not be confirmed. Reload and check the ticket before retrying.';
    default:
      return '';
  }
}
